import React, {useEffect} from 'react'
import {Box, Flex, useDisclosure, Text, Button, Input, Stack} from '../../vanilla'
import {vars} from '../../vanilla/vars.css'
import {observer} from 'mobx-react-lite'
import {HitItemProps} from '../../types/algolia'
import {ProductImage} from '../../components/product-detail/product-image'
import {PlusIcon, MinusIcon, TrashIcon} from '../../components/icons'
import {MoreInfo} from '../../components/more-info'
import {MealDealBuilderGroupState} from './meal-deal-builder-helper'

type MealDealHitItemProps = HitItemProps & {
  groupState?: MealDealBuilderGroupState
  isLastCard?: boolean
  currentBasket: Array<{productId: string | undefined; quantity: number | undefined}>
  onQuantityStateChange?: (isVisible: boolean) => void
  showQuantityReservedSpace?: boolean
}

const MealDealTileContainer = ({
  hit,
  groupState,
  isLastCard,
  currentBasket,
  onQuantityStateChange,
  showQuantityReservedSpace
}: MealDealHitItemProps) => {
  const moreInfoModal = useDisclosure()

  // @todo format the algolia data to be consistent across PLP and PDP - happens in price component too
  // @ts-ignore
  const img = hit.image_groups?.[0]?.images?.[0] || hit.imageGroups?.[0]?.images?.[0]
  const selectedQuantity = groupState?.productItems.get(hit.id) ?? 0
  const isSelected = (groupState?.productItems.get(hit.id) ?? 0) > 0
  const groupFulfilled = groupState?.remainingQuantity === 0

  const updateClickedQuantity = (nextQuantity: React.SetStateAction<number>) => {
    if (!groupState) {
      return
    }

    const resolvedQuantity =
      typeof nextQuantity === 'function' ? nextQuantity(selectedQuantity) : nextQuantity

    if (groupState.requiredProductQuantity === 1 && resolvedQuantity > 0) {
      groupState.clearSelectionsExcept(hit.id)
    }

    groupState.updateProductSelection(hit.id, resolvedQuantity)
  }

  const toggleSingleSelection = (e: React.SyntheticEvent<HTMLElement>) => {
    e.stopPropagation()
    updateClickedQuantity(isSelected ? 0 : 1)
  }

  const maximumReached =
    hit.maxOrderQuantity !== undefined &&
    (currentBasket.find((prod) => prod.productId === hit.id)?.quantity ?? 0) +
      selectedQuantity +
      1 >
      hit.maxOrderQuantity &&
    (!groupFulfilled ||
      (groupFulfilled && !isSelected && groupState?.requiredProductQuantity === 1) ||
      (groupFulfilled && groupState?.requiredProductQuantity !== 1))

  useEffect(() => {
    onQuantityStateChange?.(maximumReached)
  }, [onQuantityStateChange, maximumReached])

  const multipleSelectionATB = (
    <Flex
      alignItems="center"
      gap="8px"
      style={{
        border: `1px solid ${vars.color.gray300}`,
        borderRadius: '4px',
        backgroundColor: 'white'
      }}
    >
      {selectedQuantity > 0 && (
        <Flex alignItems="center" gap="8px">
          <Button
            width="30px"
            height="30px"
            size="icon"
            variant="unstyled"
            onClick={() => updateClickedQuantity(selectedQuantity - 1)}
            color="gray700"
            data-test-selector={selectedQuantity === 1 ? 'remove-icon' : 'decrease-quantity-button'}
          >
            {selectedQuantity === 1 ? (
              <TrashIcon boxSize="16px" />
            ) : (
              <MinusIcon boxSize="14px" />
            )}
          </Button>
          <Text data-test-selector="quantity-value" variant="text4">
            {selectedQuantity}
          </Text>
        </Flex>
      )}
      <Button
        size="icon"
        variant="unstyled"
        onClick={() => updateClickedQuantity(selectedQuantity + 1)}
        disabled={groupFulfilled || maximumReached}
        opacity={maximumReached ? '0.3' : groupFulfilled && !maximumReached ? '0.6' : '1'}
        width="30px"
        height="30px"
        color="gray700"
        style={{cursor: maximumReached || groupFulfilled ? 'not-allowed' : 'pointer'}}
        data-test-selector="increase-quantity-button"
      >
        <PlusIcon boxSize="12px" />
      </Button>
    </Flex>
  )

  const singleSelectionATB = (
    <Input
      type="radio"
      checked={isSelected}
      onChange={() => {
        // Controlled by onClick to support deselecting an already selected option.
      }}
      onClick={toggleSingleSelection}
      disabled={maximumReached}
      variant="outline"
      validatedSuccessfully={isSelected}
      opacity={maximumReached ? '0.3' : '1'}
      style={{
        borderRadius: '100%',
        display: 'block',
        width: '20px',
        height: '20px',
        padding: '0 4px',
        textAlign: 'center',
        backgroundColor: isSelected ? `${vars.color.accent1}` : 'white',
        border: `1px solid ${isSelected ? vars.color.accent1 : vars.color.gray300}`,
        boxShadow: 'white inset 0px 0px 0px 2px',
        cursor: maximumReached ? 'not-allowed' : 'pointer'
      }}
      data-test-selector="radio-button"
    />
  )

  return (
    <Stack
      data-test-selector="promo-list-item"
      bg="white"
      paddingX={['8px', '0px']}
      borderRadius={['none', 'base']}
      style={
        isLastCard
          ? {
              borderBottomLeftRadius: '4px',
              borderBottomRightRadius: '4px'
            }
          : undefined
      }
    >
      <Flex
        as="div"
        borderRadius={['none', 'base']}
        borderTop={['1px', '0px']}
        borderColor="gray100"
        bg={groupState?.productItems?.get(hit.id) ? 'lightGreen' : 'white'}
        flexDirection="row"
        alignItems="stretch"
        justifyContent="flex-start"
        paddingX="16px"
        paddingY="16px"
        gap="16px"
        marginBottom={[isLastCard ? '8px' : '0px', '0px']}
        height="full"
      >
        <Box alignSelf="flex-start" maxWidth="fifth">
          <ProductImage
            image={img}
            imageAlt={img?.alt}
            isThumbnail={false}
            inDrawer={false}
            display="block"
            draggable={false}
            height="auto"
            loading="lazy"
            style={{objectFit: 'contain'}}
            width="full"
          />
        </Box>
        <Flex
          flexDirection="column"
          justifyContent="space-between"
          alignItems="stretch"
          flexGrow="1"
          maxWidth="auto"
        >
          <Box>
            <Text variant="text3" textAlign={'left'} data-test-selector="meal-deal-product-name">
              {hit.name}
            </Text>
          </Box>
          <Flex flexDirection="column" justifyContent="flex-end" alignItems="stretch">
            <Flex
              paddingTop="12px"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              gap={['14px', '4px']}
              flexGrow="0"
            >
              <Box marginTop={['auto', '0px']} onClick={moreInfoModal.onToggle} cursor="pointer">
                <Text variant="text3" color={{default: 'accent6'}} textDecoration="underline">
                  More Info
                </Text>
              </Box>
              <Box>
                {groupState?.requiredProductQuantity === 1
                  ? singleSelectionATB
                  : multipleSelectionATB}
              </Box>
            </Flex>
            {showQuantityReservedSpace && (
              <Box marginTop="12px" minHeight="24px">
                {maximumReached && (
                  <Text variant="text2" color={{default: 'error'}}>
                    Maximum quantity reached for this product
                  </Text>
                )}
              </Box>
            )}
          </Flex>
        </Flex>
        <MoreInfo
          hit={hit}
          isOpen={moreInfoModal.isOpen}
          onClose={moreInfoModal.onToggle}
          data-test-selector="meal-deal-details-title"
        />
      </Flex>
    </Stack>
  )
}

export const MealDealProductTile = observer(MealDealTileContainer)
